// src/render/launch-fx.ts
// Full-screen "launch warning" effect when a weapon is fired.
// Intensity scales with weapon threat (damage + blast radius) so big
// nukes produce dread-inducing red vignette pulses with scanline glitch,
// while small weapons produce subtle flashes or nothing at all.

import { WEAPONS_BY_ID } from '../sim/weapons';
import { getWeaponPalette } from './particles';

interface LaunchState {
  active: boolean;
  timer: number;        // seconds remaining
  duration: number;     // total duration (seconds)
  intensity: number;    // 0..1
  r: number; g: number; b: number;   // primary flash color
  vr: number; vg: number; vb: number; // vignette color (darker, edgy)
  scanlines: boolean;   // enable scanline glitch for nukes
}

const fx: LaunchState = {
  active: false,
  timer: 0,
  duration: 1,
  intensity: 0,
  r: 255, g: 200, b: 100,
  vr: 150, vg: 20, vb: 20,
  scanlines: false,
};

/** Trigger a launch warning for the given weapon. Call once per shot. */
export function triggerLaunchFx(weaponId: string): void {
  const w = WEAPONS_BY_ID[weaponId];
  if (!w) return;

  // Threat score: blend damage and radius. Max practical ~330 (Death's Head).
  const threat = w.maxDamage + w.blastRadius * 0.8;
  const t = Math.min(1, threat / 330);

  // Skip tiny threats (tracers, baby missile, riot minor)
  if (t < 0.12) return;

  fx.active = true;
  fx.intensity = t;
  // Bigger threats linger longer
  fx.duration = 0.6 + t * 1.6;
  fx.timer = fx.duration;

  // Flash color = weapon palette fire
  const pal = getWeaponPalette(weaponId);
  fx.r = pal.fire[0];
  fx.g = pal.fire[1];
  fx.b = pal.fire[2];

  // Vignette: nukes/death's head go blood-red; others use palette ember
  const isNukeClass = weaponId === 'deaths-head' || weaponId === 'nuke' || weaponId === 'deaths-head-mirv' || weaponId === 'deaths-head-funky';
  if (isNukeClass) {
    fx.vr = 180; fx.vg = 10; fx.vb = 10;
    fx.scanlines = true;
  } else {
    fx.vr = pal.ember[0];
    fx.vg = pal.ember[1];
    fx.vb = pal.ember[2];
    fx.scanlines = t > 0.6; // only heavy hitters get scanlines
  }
}

/** Draw the launch warning overlay. Call after HUD, before banners. */
export function drawLaunchFx(
  ctx: CanvasRenderingContext2D,
  dt: number,
  w: number,
  h: number,
): void {
  if (!fx.active) return;

  fx.timer -= dt;
  if (fx.timer <= 0) {
    fx.active = false;
    return;
  }

  // Progress 1 -> 0 across lifetime
  const t = fx.timer / fx.duration;
  // Initial bright flash decays fast (first 20% of lifetime)
  const flashPhase = t > 0.8 ? (t - 0.8) / 0.2 : 0; // 1..0 during first 20%
  // Vignette pulse: sinusoidal throughout, stronger for big weapons
  const pulseFreq = 2 + fx.intensity * 3;
  const pulse = 0.5 + 0.5 * Math.sin((1 - t) * pulseFreq * Math.PI * 2);

  ctx.save();

  // 1. Full-screen flash (short, bright)
  if (flashPhase > 0) {
    const flashAlpha = flashPhase * 0.55 * fx.intensity;
    ctx.globalCompositeOperation = 'lighter';
    ctx.fillStyle = `rgba(${fx.r}, ${fx.g}, ${fx.b}, ${flashAlpha})`;
    ctx.fillRect(0, 0, w, h);
    ctx.globalCompositeOperation = 'source-over';
  }

  // 2. Vignette pulse: radial gradient darkens edges, tinted with vignette color
  const vignetteAlpha = t * fx.intensity * (0.55 + 0.25 * pulse);
  const grad = ctx.createRadialGradient(
    w / 2, h / 2, Math.min(w, h) * 0.25,
    w / 2, h / 2, Math.max(w, h) * 0.75,
  );
  grad.addColorStop(0, 'rgba(0,0,0,0)');
  grad.addColorStop(0.6, `rgba(${fx.vr}, ${fx.vg}, ${fx.vb}, ${vignetteAlpha * 0.45})`);
  grad.addColorStop(1, `rgba(${fx.vr * 0.4 | 0}, 0, 0, ${vignetteAlpha * 0.85})`);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  // 3. Scanline glitch for nuke-tier (horizontal bands that drift)
  if (fx.scanlines) {
    const lineAlpha = t * fx.intensity * 0.25;
    const drift = (1 - t) * 200;
    ctx.fillStyle = `rgba(${fx.vr}, 0, 0, ${lineAlpha})`;
    for (let y = ((drift | 0) % 6); y < h; y += 6) {
      ctx.fillRect(0, y, w, 2);
    }
  }

  ctx.restore();
}

/** Reset state (e.g., on round reset). */
export function resetLaunchFx(): void {
  fx.active = false;
  fx.timer = 0;
}
